import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  VENUE_INCLUSION_KEYS,
  compareVenues,
  type CapacityFit,
  type Venue,
  type VenueReview,
} from '@guabi/shared';
import { Button } from '@/components/ui/Button';
import { Pill } from '@/components/ui/Pill';
import { money } from '@/lib/format';
import { Stars } from './Stars';
import { useCompareNarrative } from './queries';

/**
 * The two of you, side by side, on the things that decide it.
 *
 * Everything here is computed. Nothing on this screen came from a model, and
 * that is deliberate: it is the screen you would make a decision on, so a number
 * that is occasionally wrong would be worse than no number at all.
 *
 * It opens on the shortlisted and toured venues, because a comparison of nine
 * places you have only heard of is not a comparison.
 *
 * Shown in place of the card grid rather than in a drawer: the drawer is a
 * narrow right-hand panel meant for editing one thing, and a table with a column
 * per venue needs the whole page. Three venues were already scrolling sideways
 * inside it.
 */

const FIT_TONE: Record<CapacityFit, 'attending' | 'pending' | 'declined' | 'neutral'> = {
  fits: 'attending',
  tight: 'pending',
  too_small: 'declined',
  unknown: 'neutral',
};

export function CompareVenues({
  venues,
  reviewsByVenue,
  headcount,
  wedId,
}: {
  venues: Venue[];
  reviewsByVenue: Record<string, VenueReview[]>;
  headcount: number;
  wedId: string;
}) {
  const { t } = useTranslation();
  const narrate = useCompareNarrative(wedId);

  const shortlisted = useMemo(
    () => venues.filter((v) => v.status === 'shortlisted' || v.status === 'toured'),
    [venues],
  );

  const [picked, setPicked] = useState<Set<string>>(
    () =>
      new Set((shortlisted.length > 0 ? shortlisted : venues.slice(0, 3)).map((v) => v.venueId)),
  );

  const chosen = venues.filter((v) => picked.has(v.venueId));
  const comparison = useMemo(
    () => compareVenues({ venues: chosen, reviewsByVenue, headcount }),
    [chosen, reviewsByVenue, headcount],
  );

  const toggle = (venueId: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(venueId)) next.delete(venueId);
      else next.add(venueId);
      return next;
    });

  // Only the categories somebody has actually answered for one of these venues.
  // A grid of nineteen mostly-blank rows compares nothing.
  const inclusionKeys = useMemo(() => {
    const answered = new Set(comparison.rows.flatMap((r) => r.inclusions.map((i) => i.key)));
    const catalog = VENUE_INCLUSION_KEYS.filter((k) => answered.has(k));
    const custom = [...answered].filter((k) => !VENUE_INCLUSION_KEYS.includes(k as never));
    return [...catalog, ...custom];
  }, [comparison.rows]);

  const labelFor = (key: string) =>
    key.startsWith('custom:')
      ? key.replace(/^custom:/, '').replace(/_/g, ' ')
      : t(`inclusions.${key}`);

  return (
    <section className="flex flex-col gap-5">
      <div className="flex flex-wrap gap-2">
        {venues.map((v) => (
          <button
            key={v.venueId}
            type="button"
            aria-pressed={picked.has(v.venueId)}
            onClick={() => toggle(v.venueId)}
            className={`min-h-tap rounded-pill border px-3 text-caption transition-colors ${
              picked.has(v.venueId)
                ? 'border-leaf bg-leaf text-cream'
                : 'border-line bg-paper text-leaf-soft hover:border-leaf-soft hover:text-leaf'
            }`}
          >
            {v.name}
          </button>
        ))}
      </div>

      {comparison.mixedCurrency && (
        <p className="text-caption text-status-declinedFg" role="note">
          {t('venues.mixedCurrency')}
        </p>
      )}

      {comparison.rows.length === 0 ? (
        <p className="text-body text-leaf-soft">{t('venues.comparePickSome')}</p>
      ) : (
        // The one table allowed to scroll sideways: comparing venues is
        // comparing columns, and stacking them below `md` would destroy the
        // only thing the screen is for. `rail` makes that scroll deliberate —
        // snapping, momentum, no scrollbar — rather than the accidental
        // page-pan the other tables used to cause.
        <div className="rail overflow-x-auto">
          <table className="w-full border-collapse text-body">
            <thead>
              <tr>
                <th className="sticky left-0 z-20 w-28 border-r border-line bg-paper px-2 py-2 text-left text-caption font-medium text-leaf-soft sm:w-40">
                  {t('venues.compare')}
                </th>
                {comparison.rows.map((row) => (
                  <th
                    key={row.venue.venueId}
                    className="min-w-[10rem] border-b border-line px-3 py-2 text-left align-bottom"
                  >
                    <span className="font-display text-[20px] leading-tight">{row.venue.name}</span>
                    <span className="block text-caption text-leaf-soft">
                      {t(`venues.statusValue.${row.venue.status ?? 'found'}`)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              <Row label={t('venues.realTotal')}>
                {comparison.rows.map((row) => (
                  <Cell key={row.venue.venueId}>
                    {row.cost.realTotalCents === undefined ? (
                      <span className="text-leaf-soft">—</span>
                    ) : (
                      <span className="flex flex-wrap items-baseline gap-2">
                        <span className="tabular font-medium">
                          {row.cost.isPartial
                            ? t('venues.atLeast', {
                                amount: money(row.cost.realTotalCents, row.venue.currency),
                              })
                            : money(row.cost.realTotalCents, row.venue.currency)}
                        </span>
                        {row.isCheapest && <Pill tone="attending">{t('venues.cheapest')}</Pill>}
                      </span>
                    )}
                  </Cell>
                ))}
              </Row>

              <Row label={t('venues.quote')}>
                {comparison.rows.map((row) => (
                  <Cell key={row.venue.venueId} muted>
                    {row.cost.quoteCents === undefined
                      ? '—'
                      : money(row.cost.quoteCents, row.venue.currency)}
                    {row.cost.extrasCents > 0 && (
                      <span className="block">
                        {t('venues.plusExtras', {
                          amount: money(row.cost.extrasCents, row.venue.currency),
                        })}
                      </span>
                    )}
                    {row.cost.unpriced > 0 && (
                      <span className="block">
                        {t('venues.unpriced', { count: row.cost.unpriced })}
                      </span>
                    )}
                  </Cell>
                ))}
              </Row>

              <Row label={t('venues.capacity')}>
                {comparison.rows.map((row) => (
                  <Cell key={row.venue.venueId}>
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="tabular">{row.venue.capacity ?? '—'}</span>
                      {row.capacity !== 'unknown' && (
                        <Pill tone={FIT_TONE[row.capacity]}>
                          {t(`venues.capacityFit.${row.capacity}`)}
                        </Pill>
                      )}
                    </span>
                  </Cell>
                ))}
              </Row>

              {/* One row per person, so "we disagree" is visible rather than
                  averaged away. */}
              {comparison.reviewers.map((reviewer) => (
                <Row
                  key={reviewer.userId}
                  label={reviewer.name || t('venues.member')}
                  indent
                  first={reviewer.userId === comparison.reviewers[0]?.userId}
                >
                  {comparison.rows.map((row) => {
                    const stars = row.starsByReviewer[reviewer.userId];
                    return (
                      <Cell key={row.venue.venueId}>
                        {stars === undefined ? (
                          <span className="text-caption text-leaf-soft">
                            {t('venues.notRated')}
                          </span>
                        ) : (
                          <Stars value={stars} />
                        )}
                      </Cell>
                    );
                  })}
                </Row>
              ))}

              {comparison.reviewers.length > 1 && (
                <Row label={t('venues.agreement')}>
                  {comparison.rows.map((row) => (
                    <Cell key={row.venue.venueId}>
                      {row.summary.agreementGap === null ? (
                        <span className="text-leaf-soft">—</span>
                      ) : row.summary.agreementGap > 1 ? (
                        <Pill tone="maybe">{t('venues.youDisagree')}</Pill>
                      ) : (
                        <span className="text-caption text-leaf-soft">{t('venues.youAgree')}</span>
                      )}
                    </Cell>
                  ))}
                </Row>
              )}

              <Row label={t('venues.dealbreakers')}>
                {comparison.rows.map((row) => (
                  <Cell key={row.venue.venueId}>
                    {row.summary.dealbreakers.length === 0 ? (
                      <span className="text-leaf-soft">—</span>
                    ) : (
                      <ul className="flex flex-col gap-1">
                        {row.summary.dealbreakers.map((d) => (
                          <li key={d} className="text-caption text-status-declinedFg">
                            {d}
                          </li>
                        ))}
                      </ul>
                    )}
                  </Cell>
                ))}
              </Row>

              <Row label={t('venues.tourDate')}>
                {comparison.rows.map((row) => (
                  <Cell key={row.venue.venueId} muted>
                    {row.venue.tourDate ?? '—'}
                  </Cell>
                ))}
              </Row>

              {inclusionKeys.length > 0 && (
                <tr>
                  <td
                    colSpan={comparison.rows.length + 1}
                    className="border-t border-line px-2 pb-1 pt-4"
                  >
                    <span className="eyebrow">{t('venues.whatsIncluded')}</span>
                  </td>
                </tr>
              )}

              {inclusionKeys.map((key) => (
                <Row key={key} label={labelFor(key)} indent>
                  {comparison.rows.map((row) => {
                    const item = row.inclusions.find((i) => i.key === key);
                    if (!item)
                      return (
                        <Cell key={row.venue.venueId} muted>
                          —
                        </Cell>
                      );
                    return (
                      <Cell key={row.venue.venueId}>
                        {item.status === 'included' ? (
                          <Pill tone="attending">{t('inclusions.status.included')}</Pill>
                        ) : (
                          <span className="flex flex-wrap items-center gap-1">
                            <Pill tone={item.status === 'extra' ? 'pending' : 'declined'}>
                              {t(`inclusions.status.${item.status}`)}
                            </Pill>
                            {item.estimateCents !== undefined && (
                              <span className="tabular text-caption text-leaf-soft">
                                {money(item.estimateCents, row.venue.currency)}
                              </span>
                            )}
                          </span>
                        )}
                      </Cell>
                    );
                  })}
                </Row>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* A button, never something that runs on load: it costs tokens, and the
          table above is the answer. This only puts it into sentences. */}
      {comparison.rows.length > 1 && (
        <div className="flex flex-col gap-2 border-t border-line pt-4">
          {narrate.data ? (
            <div className="panel flex flex-col gap-2 p-4">
              <p className="whitespace-pre-wrap text-body leading-relaxed">{narrate.data.text}</p>
              <p className="text-caption text-leaf-soft">{t('venues.narrativeCaveat')}</p>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="secondary"
                loading={narrate.isPending}
                onClick={() =>
                  narrate.mutate({ venueIds: comparison.rows.map((r) => r.venue.venueId) })
                }
              >
                {t('venues.explain')}
              </Button>
              <span className="text-caption text-leaf-soft">{t('venues.explainHint')}</span>
            </div>
          )}
          {narrate.isError && (
            <p role="alert" className="text-body text-status-declinedFg">
              {narrate.error.message}
            </p>
          )}
        </div>
      )}

      <p className="text-caption text-leaf-soft">
        {t('venues.compareHeadcount', { count: headcount })}
      </p>
    </section>
  );
}

function Row({
  label,
  children,
  indent = false,
  first = false,
}: {
  label: string;
  children: React.ReactNode;
  indent?: boolean;
  first?: boolean;
}) {
  return (
    <tr className={first ? 'border-t border-line' : undefined}>
      {/*
        Pinned while the venues scroll past it.
        
        This table is a column per venue and cannot fit a phone — 160px each,
        three venues, and it is already wider than the screen. That part is fine
        and intended. What was not fine is that the row label scrolled away with
        everything else, so by the third venue you were reading a number with no
        idea whether it was the price or the capacity.

        The background is opaque on purpose: the cells slide underneath it, and
        a transparent heading would have the venue names printing through it.
      */}
      <th
        scope="row"
        className={`sticky left-0 z-10 border-r border-line bg-paper px-2 py-3 text-left align-top text-caption font-medium text-leaf-soft ${
          indent ? 'pl-4' : ''
        }`}
      >
        {label}
      </th>
      {children}
    </tr>
  );
}

function Cell({ children, muted = false }: { children: React.ReactNode; muted?: boolean }) {
  return (
    <td
      className={`border-b border-line px-3 py-3 align-top ${
        muted ? 'text-caption text-leaf-soft' : ''
      }`}
    >
      {children}
    </td>
  );
}
